import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { storeScope, storedScope } from "./api.js";
import { useAuth } from "./auth.jsx";

const ScopeContext = createContext(null);

/**
 * ScopeProvider owns the tenant a member of staff is currently acting within.
 *
 * A tenant integrating over the API carries its own scope in its credential
 * and never has to choose. Staff do: they can see every tenant on the
 * platform, so "disconnect this meter" is not an answerable request until the
 * estate is named. The choice is deliberately visible in the top bar at all
 * times rather than buried in a settings screen, because the cost of acting
 * on the wrong tenant is somebody's lights going out.
 */
export function ScopeProvider({ children }) {
  const { session } = useAuth();
  const [alias, setAliasState] = useState(() => storedScope());
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const setAlias = useCallback((next) => {
    storeScope(next);
    setAliasState(next || "");
  }, []);

  useEffect(() => {
    if (!session?.token) {
      setTenants([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .listTenants()
      .then((list) => {
        if (cancelled) return;
        const all = list ?? [];
        setTenants(all);
        setError(null);

        const current = storedScope();
        const stillThere = all.some((t) => t.Alias === current);
        if (current && !stillThere) {
          // The tenant was removed, or this account can no longer see it.
          // Leaving the stale choice in place would produce a stream of
          // "not found" errors that look like a broken console.
          setAlias("");
        } else if (!current && all.length === 1) {
          // With one tenant there is no choice to make, so making the
          // operator click through a picker teaches them nothing.
          setAlias(all[0].Alias);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [session?.token, setAlias]);

  const value = useMemo(() => {
    const tenant = tenants.find((t) => t.Alias === alias) ?? null;
    return { alias, tenant, tenants, setAlias, loading, error };
  }, [alias, tenants, setAlias, loading, error]);

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function useScope() {
  const ctx = useContext(ScopeContext);
  if (!ctx) throw new Error("useScope must be used inside a ScopeProvider");
  return ctx;
}
